import React, { useEffect, useState, useRef } from "react";
import Box from "@mui/material/Box";
import { Route, Routes, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery, useTheme, Dialog, Button, Typography, IconButton, Checkbox, FormControlLabel } from "@mui/material";
import { Close, ShoppingCart } from "@mui/icons-material";
import "./App.css";

// Navbar
import MenuDeNavegacion from "./Views/navbar/navVers03/contenedor.jsx";
import MenuMobile from "./Views/navbar/navVers03/menuDeNavegacionMobile.jsx";

// Contextos
import { AuthProvider, useAuth } from "./context/AuthContext.jsx";
import { MetricasProvider } from "./context/MetricasContext.js";

// Utilidades
import ScrollToTop from "./ScrollToTop.js";

// Vistas generales (Carga normal para evitar parpadeos en inicio/login)
import Login from "./Views/login/page";
import Register from "./Views/register/page";
import DashboardPage from "./Views/dashboard/page";
import Home from "./Views/home/page.jsx";
import { AIWidgetProvider, useAIWidget } from "./context/AIWidgetContext.jsx";
import { SystemProvider } from "./context/SystemContext.jsx";
import SystemIframeContainer from "./Views/usuarios/components/tecnology/SystemIframeContainer.jsx";
import { FloatingChatManager } from "./Views/usuarios/components/mensajes/FloatingChatManager.jsx";
import Profesional from "./Views/sistemaInterno/profesional/prefesionalInicio.jsx";
import PopupCapturaLeads from "./Views/home/components/PopupCapturaLeads.jsx";

// Componentes Lazy (Carga diferida para optimizar el bundle inicial)
const ForgotPassword = React.lazy(() => import("./Views/login/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./Views/login/ResetPassword"));
const RecuperarPassword = React.lazy(() => import("./Views/authPopup/RecuperarPassword.jsx"));
const Portfolio = React.lazy(() => import("./Views/portfolio/page.jsx"));
const Nosotros = React.lazy(() => import("./Views/nosotros/page.jsx"));
const Contact = React.lazy(() => import("./Views/contact/page.jsx"));
const Servicios = React.lazy(() => import("./Views/servicios/page.jsx"));
const DynamicServiceView = React.lazy(() => import("./Views/servicios/DynamicServiceView.jsx"));
const Soluciones = React.lazy(() => import("./Views/soluciones/page.jsx"));
const CursosEnLinea = React.lazy(() => import("./Views/soluciones/CursosEnLinea.jsx"));

const DiseñoWeb = React.lazy(() => import("./Views/internalPages/diseñoWeb/pages.jsx"));
const Marketing = React.lazy(() => import("./Views/internalPages/Marketing/pages.jsx"));

const PrivacyPolicy = React.lazy(() => import("./Views/pageLegals/PrivacyPolicy.jsx"));
const Disclaimer = React.lazy(() => import("./Views/pageLegals/disclaimer.jsx"));
const Terms = React.lazy(() => import("./Views/pageLegals/termsandconditions.jsx"));
const Copyright = React.lazy(() => import("./Views/pageLegals/Copyright.jsx"));

const DynamicServicePricingView = React.lazy(() => import("./Views/servicios/DynamicServicePricingView.jsx"));

const ComunidadDeMarketing = React.lazy(() => import("./Views/comunidad/marketing.jsx"));
const ComunidadDeComercio = React.lazy(() => import("./Views/comunidad/comercio.jsx"));
const ComunidadDeTecnologia = React.lazy(() => import("./Views/comunidad/tecnologia.jsx"));

const DatosEmpresaForm = React.lazy(() => import("./Views/usuarios/empresas/DatosEmpresaForm.jsx"));
const AffiliateProfilePage = React.lazy(() => import("./Views/usuarios/perfilDelUsuario/perfilDeUsuarioHitpoly.jsx"));
const StorageManager = React.lazy(() => import("./Views/usuarios/almacenamiento/StorageManager.jsx"));
const ExternalSystemPage = React.lazy(() => import("./Views/usuarios/components/tecnology/ExternalSystemPage.jsx"));
const PeopleNetworkPage = React.lazy(() => import("./Views/usuarios/components/community/PeopleNetworkPage.jsx"));
const RedEmpresasPage = React.lazy(() => import("./Views/usuarios/components/community/RedEmpresasPage.jsx"));
const AllCorporateServices = React.lazy(() => import("./Views/usuarios/components/community/widgets/AllCorporateServices.jsx"));
const AllProfessionalServices = React.lazy(() => import("./Views/usuarios/components/community/widgets/AllProfessionalServices.jsx"));
const ProfessionalHistoryPC = React.lazy(() => import("./Views/navbar/navVers03/ProfessionalHistoryPC.jsx"));
const GlobalSearchResultsPage = React.lazy(() => import("./Views/usuarios/components/search/GlobalSearchResultsPage.jsx"));


// =====================
//   PROTECTED ROUTE
// =====================
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/" />;
};

// Sistemas que viven dentro del propio holding (no iframes externos)
// ============================================
// SISTEMAS EXTERNOS (IFRAMES)
// Todos: bolsa, academia, crm, proyectos.
// Deben usar el patrón iframe-route-pattern con
// routes.jsx, urlRecovery.js y historyGuard.js.
// ============================================
const INTERNAL_SYSTEM_IDS = ["comunidad", "mensajes"];

// Devuelve el id del sistema externo (iframes) al que pertenece una ruta, o null si no aplica
const getExternalSystemId = (path) => {
  const clean = (path || "").split("?")[0];
  const m = clean.match(/^\/systems\/([^/]+)/);
  if (!m) return null;
  return INTERNAL_SYSTEM_IDS.includes(m[1]) ? null : m[1];
};

// Indica si una ruta pertenece a la comunidad (/systems/comunidad...)
const isCommunityPath = (path) => {
  const clean = (path || "").split("?")[0];
  return clean === "/systems/comunidad" || clean.startsWith("/systems/comunidad/");
};

// Última URL que el holding fijó explícitamente (vía pushState/replaceState).
// Se actualiza de forma síncrona con wrappers, de modo que el handler de popstate
// puede conocer SIEMPRE la URL previa al "atrás" sin depender del estado de React Router
// (que no ve los replaceState de UPDATE_PARENT_URL y compite con los efectos).
let lastParentUrl = window.location.pathname + window.location.search;

const origPushState = window.history.pushState.bind(window.history);
const origReplaceState = window.history.replaceState.bind(window.history);
window.history.pushState = (state, title, url) => {
  if (url) lastParentUrl = String(url);
  return origPushState(state, title, url);
};
window.history.replaceState = (state, title, url) => {
  if (url) lastParentUrl = String(url);
  return origReplaceState(state, title, url);
};

// =====================
//     APP CONTENT
// =====================
function AppContent() {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { isAssistantOpen, closeAssistant } = useAIWidget();
  const [planConflict, setPlanConflict] = useState(null);
  const [planExists, setPlanExists] = useState(false);
  const [replaceMode, setReplaceMode] = useState(false);
  const [selectedReplaceIds, setSelectedReplaceIds] = useState([]);

  const openPlanConflict = (existingItems, newProduct) => {
    setPlanConflict({ existingItems, newProduct });
    setReplaceMode(false);
    setSelectedReplaceIds([]);
  };

  // Ruta (pathname+search) anterior, para detectar navegación hacia atrás dentro de un sistema externo
  const prevLocationRef = useRef(null);
  // Ruta desde la cual se entró al sistema externo actual (p.ej. la comunidad)
  const systemEntryRef = useRef("/");
  // Contador de clics "atrás" consecutivos dentro de un sistema externo (debug)
  const backClicksRef = useRef(0);

  const CART_API = "https://apiweb.hitpoly.com/ajax/carritoController.php";
  const SERVICES_API = "https://apiweb.hitpoly.com/ajax/servicesController.php";

  const normalizeProductoId = (id) => String(id).replace('serv_', '');

  // ¿Este item del carrito corresponde al mismo plan que `product`?
  // 1. Coincidencia exacta de producto_id (formato nuevo: servicio_contrato_plan)
  // 2. Legado: mismo servicio + mismo nombre de plan (items guardados con id sin nombre)
  const itemMatchesPlan = (item, product) => {
    if (normalizeProductoId(item.producto_id) === normalizeProductoId(product.id)) return true;
    const itemBase = String(item.producto_id).split('_')[0].replace('serv_', '');
    const productBase = String(product.servicio_base_id || product.id).split('_')[0].replace('serv_', '');
    if (itemBase !== productBase) return false;
    const itemNombre = (item.nombre || '').toString().trim().toLowerCase();
    const productNombre = (product.name || '').toString().trim().toLowerCase();
    return Boolean(itemNombre && productNombre && itemNombre === productNombre);
  };

  const handleCheckPlanExists = async (event, product) => {
    const currentUserId = user?.id || user?.user_id;
    let exists = false;
    try {
      if (currentUserId && product?.id) {
        const cartRes = await fetch(CART_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ accion: "getItems", usuario_id: currentUserId }),
        });
        const cartData = await cartRes.json();
        const items = cartData.items || [];
        const newProductoId = normalizeProductoId(product.id);
        exists = items.some(item => itemMatchesPlan(item, product));
        console.log("[CARRITO][ContratarServicio] ¿plan en carrito?", {
          plan: newProductoId,
          enCarrito: exists,
          itemsEnCarrito: items.map(i => i.producto_id),
        });
      }
    } catch (e) {
      console.warn("[ADD2CART][Holding] Error al verificar plan en carrito:", e);
    }
    try {
      if (event.source && event.source.postMessage) {
        event.source.postMessage({ type: 'PLAN_EXISTS_RESPONSE', exists, product }, '*');
      }
    } catch (e) {
      console.warn("[ADD2CART][Holding] No se pudo responder al iframe:", e);
    }
  };

  // Resuelve el nombre del servicio (company_services.title) desde el backend.
  // Se usa como respaldo cuando el iframe desplegado aún no envía servicio_nombre.
  const resolveServicioNombre = async (empresaId, serviceId) => {
    if (!empresaId || !serviceId) return "";
    try {
      const res = await fetch(SERVICES_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accion: "get_services_list", empresa_id: empresaId }),
      });
      const json = await res.json();
      const found = (json?.data || []).find(s => String(s.id) === String(serviceId));
      return found?.title || "";
    } catch (e) {
      console.warn("[ADD2CART][Holding] No se pudo resolver el nombre del servicio:", e);
      return "";
    }
  };

  const addItemToCart = async (currentUserId, product) => {
    const body = {
      accion: "addItem",
      usuario_id: currentUserId,
      producto_id: String(product.id).replace('serv_', ''),
      tipo: product.type || 'servicio',
      nombre: product.name,
      descripcion: product.details || '',
      imagen: product.image || '',
      precio_original: product.price_original,
      precio_final: product.price_final,
      origen: product.origen || 'bolsa',
      tipo_contrato: product.tipoContrato || '',
      servicio_nombre: product.servicio_nombre || '',
      tipo_servicio: product.tipo_servicio || '',
      beneficios: product.beneficios ? JSON.stringify(product.beneficios) : ''
    };
    const resp = await fetch(CART_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    const json = await resp.json();
    return json;
  };

  // Registrar la ruta desde la cual se entró a un sistema externo.
  // El "atrás" (navegador o app) solo lleva a la comunidad si el usuario
  // realmente vino de la comunidad. Si entró desde otro sistema o desde
  // cualquier otra ruta, se deja null para que el "atrás" use el historial propio.
  useEffect(() => {
    const prevPath = prevLocationRef.current;
    const currentPath = location.pathname + location.search;
    const prevSys = prevPath ? getExternalSystemId(prevPath) : null;
    const currentSys = getExternalSystemId(currentPath);

    if (!prevSys && currentSys) {
      // Entrando a un sistema externo desde una ruta no-sistema (comunidad, raíz, perfil...)
      systemEntryRef.current = isCommunityPath(prevPath || '/') ? prevPath : null;
    } else if (prevSys && currentSys && prevSys !== currentSys) {
      // Cambiando entre sistemas externos: no venimos de la comunidad
      systemEntryRef.current = null;
    } else if (!currentSys) {
      // Fuera de un sistema externo: limpiamos la referencia
      systemEntryRef.current = null;
    }
    // Misma navegación dentro del mismo sistema externo: se conserva el entry.

    prevLocationRef.current = currentPath;
  }, [location.pathname, location.search]);

  // Botón "atrás" del navegador: si aterriza en otra página del mismo sistema externo,
  // salimos directamente a la entrada guardada (comunidad) en vez de quedarnos en ella.
  useEffect(() => {
    const onPopState = () => {
      const nextPath = window.location.pathname + window.location.search;
      const prevUrl = lastParentUrl;
      const prevSys = getExternalSystemId(prevUrl);
      const nextSys = getExternalSystemId(nextPath);
      const entry = systemEntryRef.current;
      const exitToEntry = prevSys && nextSys && prevSys === nextSys && entry && entry !== '/' && !getExternalSystemId(entry);

      backClicksRef.current += 1;

      if (exitToEntry) {
        setTimeout(() => navigate(entry, { replace: true }), 0);
      }
      if (!nextSys) {
        backClicksRef.current = 0;
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Escuchar mensajes de la academia y bolsa (iframes)
  useEffect(() => {
    const handleMessage = async (event) => {
      if (!event.data?.type || event.data.type === 'webpackWarnings') return;

      if (event.data.type === 'OPEN_CART') {

        // Disparar evento personalizado que el CarritoDrawer escuchará
        window.dispatchEvent(new CustomEvent('open-cart-drawer'));
      }

      if (event.data.type === 'UPDATE_PARENT_URL') {
        const { params } = event.data;
        
        if (params) {
          const systemId = params.system_id || window.location.pathname.split('/')[2];
          
          if (systemId) {
            const internalRoute = params.route || params.path || (params.view ? `/${params.view}` : '');
    
            const newPath = `/systems/${systemId}${internalRoute.startsWith('/') ? internalRoute : (internalRoute ? '/' + internalRoute : '')}`;
            
            const searchParams = new URLSearchParams(window.location.search);
            Object.keys(params).forEach(key => {
               if (key !== 'route' && key !== 'view' && key !== 'path' && key !== 'system_id' && key !== 'forceSync') {
                 if (params[key]) searchParams.set(key, params[key]);
                 else searchParams.delete(key);
               }
            });
            
            const searchStr = searchParams.toString();
            const finalUrl = searchStr ? `${newPath}?${searchStr}` : newPath;
            
            const currentUrl = window.location.pathname + window.location.search;
            const currentPath = currentUrl.split('?')[0];
            const isCurrentDeep = currentPath !== `/systems/${systemId}` && currentPath !== `/systems/${systemId}/`;
            const canSync = !isCurrentDeep || currentPath === newPath.split('?')[0] || params.forceSync === true;
            // Solo sincronizar la URL si:
            //  1) Seguimos DENTRO de un sistema externo (si ya salimos a la comunidad, ignorar mensajes tardíos).
            //  2) El padre está en la raíz del sistema (el iframe puede restaurar ruta guardada), O
            //     la ruta del mensaje coincide con la que ya tiene el padre.
            // Esto evita que las navegaciones internas del iframe (relacionados, siguiente servicio, etc.)
            // o los syncs tardíos de una sesión anterior sobreescriban la URL real del usuario.
            if (currentUrl !== finalUrl && getExternalSystemId(currentUrl) && canSync) {
              window.history.replaceState({ fromIframe: true }, '', finalUrl);
            }
          }
        }
      }

      if (event.data.type === 'CHECK_PLAN_EXISTS') {
        await handleCheckPlanExists(event, event.data.product);
        return;
      }

      if (event.data.type === 'OPEN_CART') {
        window.dispatchEvent(new CustomEvent('open-cart-drawer'));
        return;
      }

      if (event.data.type === 'ADD_TO_CART') {
        const { product } = event.data;
        const currentUserId = user?.id || user?.user_id;

        if (isAuthenticated && currentUserId && product) {
          // Garantizar el nombre del servicio: el iframe desplegado puede llegar con servicio_nombre vacío.
          if (!product.servicio_nombre) {
            const m = String(window.location.href).match(/empresa\/(\d+)\/servicio-empresa\/(\d+)/);
            if (m) {
              product.servicio_nombre = await resolveServicioNombre(m[1], m[2]);
            }
          }

          try {
            const cartRes = await fetch(CART_API, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ accion: "getItems", usuario_id: currentUserId }),
            });
            const cartData = await cartRes.json();
            const items = cartData.items || [];

            const baseServiceId = String(product.servicio_base_id || product.id).replace('serv_', '');
            const existingItems = items.filter(item => {
              const itemBaseId = String(item.producto_id).split('_')[0].replace('serv_', '');
              return itemBaseId === baseServiceId;
            });
            const existingSamePlan = existingItems.find(item => itemMatchesPlan(item, product));

            if (existingSamePlan) {
              setPlanExists(true);
              return;
            }

            if (existingItems.length > 0) {
              openPlanConflict(existingItems, product);
              return;
            }
          } catch (e) {
            console.warn("[ADD2CART][Holding] Error al obtener el carrito, se continúa con el add:", e);
            // If cart fetch fails, proceed with add
          }

          try {
            const respData = await addItemToCart(currentUserId, product);
            if (respData.status === "success" || respData.status === "info") {
              if (respData.status === "info") {
                alert(respData.message || "Este producto ya está en tu carrito.");
              }
              window.dispatchEvent(new CustomEvent('open-cart-drawer'));
            } else {
              console.warn("[HOLDING APP] No se abrió el carrito porque la API respondió:", respData.message);
              if (respData.message) alert(respData.message);
            }
          } catch (e) {
            console.error("[HOLDING APP] Error añadiendo al carrito:", e);
          }
        } else {
          console.warn("[ADD2CART][Holding] No se pudo añadir al carrito. Usuario no autenticado o faltan datos.", {
            isAuthenticated,
            currentUserId,
            hasProduct: Boolean(product),
          });
        }
      }

      // Handler para copiar texto desde iframes (soluciona problemas de permisos de portapapeles)
      if (event.data.type === 'COPY_TEXT' && event.data.text) {
        try {
          await navigator.clipboard.writeText(event.data.text);
        } catch (err) {
          // Fallback tradicional en el padre
          const textArea = document.createElement("textarea");
          textArea.value = event.data.text;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
      }
      // Handler para navegación desde iframes (blog, etc.)
      if (event.data.type === 'NAVIGATE' && event.data.path) {
        const path = event.data.path;
        if (/^https?:\/\//i.test(path)) {
          // URL absoluta externa: recarga completa
          window.location.href = path;
        } else if (path.startsWith('/')) {
          // Ruta relativa: SPA navigate (sin recargar hitpoly)
          navigate(path);
        } else {
          window.location.href = `https://hitpoly.com/${path}`;
        }
      }
      // Handler para "atrás" interno de un sistema externo (bolsa/academy): sale del sistema
      if (event.data.type === 'SYSTEM_BACK') {
        const entry = systemEntryRef.current;
        if (entry && entry !== '/' && !getExternalSystemId(entry)) {
          navigate(entry, { replace: true });
        } else {
          window.history.back();
        }
      }
      // Handler para "atrás" del navegador que consumió una entrada interna del iframe:
      // el iframe avisa que volvió a una página interna -> salimos a la entrada guardada
      if (event.data.type === 'SYSTEM_POP') {
        const entry = systemEntryRef.current;
        const inExternal = getExternalSystemId(window.location.pathname + window.location.search);
        if (inExternal && entry && entry !== '/' && !getExternalSystemId(entry)) {
          navigate(entry, { replace: true });
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isAuthenticated, user, navigate]);

  const removeCartItem = async (currentUserId, itemId) => {
    await fetch(CART_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ accion: "removeItem", usuario_id: currentUserId, item_id: itemId }),
    });
  };

  const buildReAddProduct = (item, ts, servicioNombreFallback) => ({
    id: `${item.producto_id}__dup_${ts}`,
    servicio_base_id: item.producto_id,
    servicio_nombre: item.servicio_nombre || servicioNombreFallback || '',
    name: item.nombre,
    price_original: item.precio_original,
    price_final: item.precio_final,
    type: item.tipo || 'servicio',
    origen: item.origen || 'bolsa',
    details: item.descripcion || '',
    image: item.imagen || '',
    tipoContrato: item.tipo_contrato || '',
    beneficios: Array.isArray(item.beneficios) ? item.beneficios : [],
  });

  // "Comprar este plan también": conserva todos los planes actuales y añade el nuevo.
  // Los planes con el mismo producto_id que el nuevo se quitan y se re-agregan con id único
  // para que el dedup del backend no bloquee la adición.
  const handleAddNewPlan = async () => {
    if (!planConflict) return;
    const currentUserId = user?.id || user?.user_id;
    const newId = String(planConflict.newProduct.id).replace('serv_', '');
    const ts = Date.now();
    try {
      let index = 0;
      for (const item of planConflict.existingItems) {
        const itemId = String(item.producto_id).replace('serv_', '');
        if (itemId === newId) {
          await removeCartItem(currentUserId, item.id);
          await addItemToCart(currentUserId, buildReAddProduct(item, `${ts}_${index++}`, planConflict.newProduct.servicio_nombre));
        }
      }
      const respData = await addItemToCart(currentUserId, planConflict.newProduct);
      if (respData.status === "success" || respData.status === "info") {
        window.dispatchEvent(new CustomEvent('open-cart-drawer'));
      } else {
        console.warn("[HOLDING APP] Error al agregar el plan adicional. API:", respData.message);
      }
    } catch (e) {
      console.error("[HOLDING APP] Error al agregar el plan adicional:", e);
    } finally {
      setPlanConflict(null);
      setReplaceMode(false);
      setSelectedReplaceIds([]);
    }
  };

  // "Reemplazar seleccionados": quita los planes marcados y añade el nuevo.
  // Con un solo plan en carrito se pasa la lista explícita y no se usa el modo marcado.
  const handleReplaceSelected = async (idsOverride) => {
    if (!planConflict) return;
    const currentUserId = user?.id || user?.user_id;
    const ids = idsOverride || selectedReplaceIds;
    try {
      const toRemove = planConflict.existingItems.filter(item => ids.includes(String(item.id)));
      for (const item of toRemove) {
        await removeCartItem(currentUserId, item.id);
      }
      const respData = await addItemToCart(currentUserId, planConflict.newProduct);
      if (respData.status === "success" || respData.status === "info") {
        window.dispatchEvent(new CustomEvent('open-cart-drawer'));
      } else {
        console.warn("[HOLDING APP] Error al reemplazar planes. API:", respData.message);
      }
    } catch (e) {
      console.error("[HOLDING APP] Error al reemplazar planes:", e);
    } finally {
      setPlanConflict(null);
      setReplaceMode(false);
      setSelectedReplaceIds([]);
    }
  };

  const toggleReplaceId = (id) => {
    setSelectedReplaceIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Rutas fijas donde se oculta el menú principal
  const hideMenuRoutes = [
    "/dashboardsistem",
    "/perfil-de-usuario",
    "/almacenamiento",
    "/MyNegocio",
    "/datos-de-empresa",
    "/dashboard/comunidad",
    "/red-personas",
    "/red-empresas",
    "/servicios-empresas",
    "/servicios-profesionales",
    "/history/professional",
    "/search/top",
    "/usuario",
  ];

  // Rutas públicas para la lógica de perfiles dinámicos
  const knownPublicRoutes = [
    "/login",
    "/register",
    "/dashboard",
    "/portfolio",
    "/nosotros",
    "/servicios",
    "/contact",
    "/soluciones",
    "/DiseñoWeb",
    "/Marketing",
    "/privacypolicy",
    "/disclaimer",
    "/termsandconditions",
    "/Copyright",
  ];

  // --- LÓGICA DE DETECCIÓN DINÁMICA ---

  // Detectar si estamos en una ruta de sistema externo (Iframe)
  const isExternalSystem = location.pathname.startsWith("/systems/");

  const isDynamicProfile =
    location.pathname.split("/").length === 2 && location.pathname !== "/";

  // Ocultamos menú si:
  // 1. Está en la lista fija
  // 2. Es una ruta /systems/...
  // 3. Es un perfil dinámico no público
  // 4. Es la raíz y está autenticado
  const hideMenu =
    hideMenuRoutes.includes(location.pathname) ||
    isExternalSystem ||
    (isDynamicProfile && !knownPublicRoutes.includes(location.pathname)) ||
    (location.pathname === "/" && isAuthenticated) ||
    new URLSearchParams(location.search).get("preview") === "true";

  return (
    <>
      <ScrollToTop />
      <PopupCapturaLeads
        open={isAssistantOpen}
        onClose={closeAssistant}
      />

      {/* MOTOR GLOBAL DE SISTEMAS (Para persistencia de Iframes) */}
      {(isAuthenticated || isExternalSystem) && <SystemIframeContainer />}

      {/* CHAT FLOTANTE GLOBAL */}
      <FloatingChatManager />

      {!hideMenu && (
        <Box
          sx={{
            backgroundColor: "#fff",
            position: "fixed",
            top: "0px",
            zIndex: 100000,
            width: "100%",
            height: "65px",
            boxShadow: "0px -4px 8px rgba(0, 0, 0, 0.5)",
          }}
        >
          {isLargeScreen ? <MenuDeNavegacion /> : <MenuMobile />}
        </Box>
      )}

      <Box
        sx={{
          position: "absolute",
          top: hideMenu ? 0 : { xs: "50px", md: "0px" },
          width: "100%",
        }}
      >
        <React.Suspense fallback={<Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0f0f1a' }}><Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}><Box sx={{ width: 72, height: 72, borderRadius: '16px', backgroundImage: 'url(/images/isotipo-hitpoly.png)', backgroundSize: 'contain', boxShadow: '0 8px 40px rgba(108,77,226,0.5)', animation: 'splash-float 2s ease-in-out infinite', '@keyframes splash-float': { '0%, 100%': { transform: 'translateY(0)' }, '50%': { transform: 'translateY(-8px)' } } }} /><Box sx={{ mt: 3, color: '#fff', fontWeight: 700, fontSize: 18, letterSpacing: 1 }}>HitPoly</Box><Box sx={{ mt: 1, color: '#6a7085', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase' }}>Cargando...</Box></Box></Box>}>
          <Routes>
            {/* RUTA RAIZ */}
            <Route
              path="/"
              element={isAuthenticated ? <Profesional /> : <Home />}
            />

            {/* Públicas */}
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/nosotros" element={<Nosotros />} />
            <Route path="/servicios" element={<Servicios />} />
            <Route path="/servicios/:empresaId/:slug" element={<DynamicServiceView />} />
            <Route path="/servicios/:empresaId/:slug/precios" element={<DynamicServicePricingView />} />
            <Route path="/contact" element={<Contact />} />

            {/* Auth */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/recuperar" element={<RecuperarPassword />} />
            <Route path="/registro" element={<Register />} />
            <Route path="/register" element={<Register />} />
            <Route path="/dashboard" element={<DashboardPage />} />

            {/* Informativas */}
            <Route path="/DiseñoWeb" element={<DiseñoWeb />} />
            <Route path="/Marketing" element={<Marketing />} />
            <Route path="/privacypolicy" element={<PrivacyPolicy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/termsandconditions" element={<Terms />} />
            <Route path="/Copyright" element={<Copyright />} />

            {/* Soluciones */}
            <Route path="/soluciones" element={<Soluciones />} />
            <Route path="/soluciones/crecimiento-digital" element={<Servicios />} />
            <Route path="/soluciones/cursos-en-linea" element={<CursosEnLinea />} />
            {/* Comunidad */}
            <Route path="/comunidades/marketing" element={<ComunidadDeMarketing />} />
            <Route path="/comunidades/comercio" element={<ComunidadDeComercio />} />
            <Route path="/comunidades/tecnologia" element={<ComunidadDeTecnologia />} />
            <Route path="/history/professional" element={<ProtectedRoute><ProfessionalHistoryPC /></ProtectedRoute>} />
            <Route path="/search/top" element={<ProtectedRoute><GlobalSearchResultsPage /></ProtectedRoute>} />

            {/* Rutas Protegidas */}
            <Route path="/dashboardsistem" element={<ProtectedRoute><Profesional /></ProtectedRoute>} />
            <Route path="/MyNegocio" element={<ProtectedRoute><Profesional /></ProtectedRoute>} />
            <Route path="/datos-de-empresa" element={<ProtectedRoute><DatosEmpresaForm /></ProtectedRoute>} />
            <Route path="/almacenamiento" element={<ProtectedRoute><StorageManager /></ProtectedRoute>} />
            <Route path="/perfil-de-usuario" element={<ProtectedRoute><AffiliateProfilePage /></ProtectedRoute>} />
            <Route path="/usuario" element={<ProtectedRoute><AffiliateProfilePage /></ProtectedRoute>} />
            <Route path="/dashboard/comunidad" element={<ProtectedRoute><ExternalSystemPage /></ProtectedRoute>} />
            <Route path="/systems/comunidad" element={<ProtectedRoute><ExternalSystemPage /></ProtectedRoute>} />

            {/* VISOR DE SISTEMAS EXTERNOS (NUEVA RUTA DINÁMICA CON WILDCARD) */}
            <Route path="/systems/:systemId/*" element={<ExternalSystemPage />} />

            {/* RED DE PERSONAS Y EMPRESAS */}
            <Route path="/red-personas" element={<ProtectedRoute><PeopleNetworkPage /></ProtectedRoute>} />
            <Route path="/red-empresas" element={<ProtectedRoute><RedEmpresasPage /></ProtectedRoute>} />
            <Route path="/servicios-empresas" element={<ProtectedRoute><AllCorporateServices /></ProtectedRoute>} />
            <Route path="/servicios-profesionales" element={<ProtectedRoute><AllProfessionalServices /></ProtectedRoute>} />

            {/* PERFIL DINÁMICO (SIEMPRE AL FINAL) */}
            <Route
              path="/:username"
              element={<AffiliateProfilePage key={window.location.pathname} />}
            />
          </Routes>
        </React.Suspense>
      </Box>

      {/* Modal de Conflicto de Planes */}
      <Dialog
        open={Boolean(planConflict)}
        onClose={() => { setPlanConflict(null); setReplaceMode(false); setSelectedReplaceIds([]); }}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 560, width: "90%" } }}
        sx={{ zIndex: 300000 }}
      >
        {planConflict && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                {replaceMode
                  ? "Elige qué planes reemplazar"
                  : planConflict.existingItems.length === 1
                    ? "Plan en tu carrito"
                    : "Planes en tu carrito"}
              </Typography>
              <IconButton
                size="small"
                onClick={() => { setPlanConflict(null); setReplaceMode(false); setSelectedReplaceIds([]); }}
              >
                <Close />
              </IconButton>
            </Box>

            {!replaceMode ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Ya tienes {planConflict.existingItems.length} {planConflict.existingItems.length === 1 ? "plan" : "planes"} de este servicio en tu carrito. ¿Quieres comprar también este plan o cambiar por el nuevo?
                </Typography>

                {/* Planes actuales en carrito */}
                <Box sx={{ p: 2, bgcolor: "#fff4f4", borderRadius: 2, mb: 2, border: "1px solid #ffe0e0" }}>
                  <Typography variant="caption" fontWeight="bold" color="error" sx={{ mb: 1, display: "block" }}>
                    {planConflict.existingItems.length === 1 ? "Plan actual en tu carrito" : "Planes actuales en tu carrito"}
                  </Typography>
                  {planConflict.existingItems.map(item => (
                    <Box key={String(item.id)} sx={{ display: "flex", gap: 2, mb: 1.5 }}>
                      <Box
                        component="img"
                        src={item.imagen}
                        sx={{ width: 56, height: 56, borderRadius: 1.5, objectFit: "cover", bgcolor: "#eee" }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="bold">{item.nombre}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {item.tipo_contrato}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" color="#F21C63">
                          ${parseFloat(item.precio_final).toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Nuevo plan seleccionado */}
                <Box sx={{ p: 2, bgcolor: "#f0fdf4", borderRadius: 2, mb: 3, border: "1px solid #dcfce7" }}>
                  <Typography variant="caption" fontWeight="bold" color="success.main" sx={{ mb: 1, display: "block" }}>
                    Nuevo plan seleccionado
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Box
                      component="img"
                      src={planConflict.newProduct.image}
                      sx={{ width: 56, height: 56, borderRadius: 1.5, objectFit: "cover", bgcolor: "#eee" }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="bold">{planConflict.newProduct.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {planConflict.newProduct.tipoContrato}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="#16a34a">
                        ${parseFloat(planConflict.newProduct.price_final).toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<ShoppingCart />}
                    onClick={handleAddNewPlan}
                    sx={{ bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" }, borderRadius: 2, py: 1.5 }}
                  >
                    Comprar también este plan (${(planConflict.existingItems.reduce((a, i) => a + parseFloat(i.precio_final), 0) + parseFloat(planConflict.newProduct.price_final)).toFixed(2)})
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="warning"
                    onClick={planConflict.existingItems.length === 1
                      ? () => handleReplaceSelected([String(planConflict.existingItems[0].id)])
                      : () => setReplaceMode(true)}
                    sx={{ borderRadius: 2, py: 1.5 }}
                  >
                    {planConflict.existingItems.length === 1
                      ? `Cambiar a ${planConflict.newProduct.name} ($${parseFloat(planConflict.newProduct.price_final).toFixed(2)})`
                      : "Cambiar plan (elegir cuál)"}
                  </Button>
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => { setPlanConflict(null); setReplaceMode(false); setSelectedReplaceIds([]); }}
                    sx={{ borderRadius: 2, color: "text.secondary" }}
                  >
                    Cancelar
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Marca qué planes quieres reemplazar por <strong>{planConflict.newProduct.name}</strong>:
                </Typography>

                {/* Lista seleccionable de planes actuales */}
                <Box sx={{ p: 2, bgcolor: "#fff4f4", borderRadius: 2, mb: 2, border: "1px solid #ffe0e0" }}>
                  {planConflict.existingItems.map(item => {
                    const itemKey = String(item.id);
                    const checked = selectedReplaceIds.includes(itemKey);
                    return (
                      <FormControlLabel
                        key={itemKey}
                        control={
                          <Checkbox
                            checked={checked}
                            onChange={() => toggleReplaceId(itemKey)}
                            color="error"
                          />
                        }
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                            <Box
                              component="img"
                              src={item.imagen}
                              sx={{ width: 40, height: 40, borderRadius: 1.5, objectFit: "cover", bgcolor: "#eee" }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight="bold">{item.nombre}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                {item.tipo_contrato}
                              </Typography>
                            </Box>
                            <Typography variant="body2" fontWeight="bold" color="#F21C63">
                              ${parseFloat(item.precio_final).toFixed(2)}
                            </Typography>
                          </Box>
                        }
                        sx={{ width: "100%", alignItems: "center", mx: 0, mb: 0.5, borderRadius: 1.5, "&:hover": { bgcolor: "#ffe9e9" } }}
                      />
                    );
                  })}
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: "block" }}>
                  El nuevo plan será añadido: {planConflict.newProduct.name} · {planConflict.newProduct.tipoContrato} (${parseFloat(planConflict.newProduct.price_final).toFixed(2)})
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={selectedReplaceIds.length === 0}
                    onClick={() => handleReplaceSelected()}
                    sx={{ bgcolor: "#F21C63", "&:hover": { bgcolor: "#d11752" }, borderRadius: 2, py: 1.5 }}
                  >
                    {selectedReplaceIds.length === 0
                      ? "Selecciona qué planes reemplazar"
                      : `Reemplazar ${selectedReplaceIds.length} ${selectedReplaceIds.length === 1 ? "plan" : "planes"} con ${planConflict.newProduct.name} ($${parseFloat(planConflict.newProduct.price_final).toFixed(2)})`}
                  </Button>
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => setReplaceMode(false)}
                    sx={{ borderRadius: 2, color: "text.secondary" }}
                  >
                    Volver
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </Dialog>

      {/* Aviso: el mismo plan ya está en el carrito */}
      <Dialog
        open={planExists}
        onClose={() => setPlanExists(false)}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 480, width: "90%" } }}
        sx={{ zIndex: 300000 }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">Este plan ya está en tu carrito</Typography>
            <IconButton size="small" onClick={() => setPlanExists(false)}><Close /></IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            El plan seleccionado de este mismo producto ya existe en tu carrito. Selecciona otro plan para cambiarlo o agrega un plan adicional, o ve al carrito para terminar tu compra.
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<ShoppingCart />}
              onClick={() => {
                setPlanExists(false);
                window.dispatchEvent(new CustomEvent('open-cart-drawer'));
              }}
              sx={{ bgcolor: "#F21C63", "&:hover": { bgcolor: "#d11752" }, borderRadius: 2, py: 1.5 }}
            >
              Ir al carrito
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={() => setPlanExists(false)}
              sx={{ borderRadius: 2, color: "text.secondary" }}
            >
              Cerrar
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}

// =====================
//         APP
// =====================
function App() {
  return (
    <AuthProvider>
      <MetricasProvider>
        <SystemProvider>
          <AIWidgetProvider>
            <AppContent />
          </AIWidgetProvider>
        </SystemProvider>
      </MetricasProvider>
    </AuthProvider>
  );
}

export default App;